import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Radio, Users, Eye, Home, Circle, CheckCircle2, Search, Star } from 'lucide-react';
import { t } from '../../utils/i18n';
import { useDmPersona } from '../../context/DmPersonaContext';
import { getCelebRoomsFeed } from '../../api/celebRoom';
import UrlAvatar from './UrlAvatar';

const PAGE_SIZE = 20;

// Sort keys must match the backend (get-celeb-rooms-feed `sort` param).
const SORT_OPTIONS = ['fans', 'views', 'recent'];
// Client-side fan filter over the loaded pages (requirement: UI에서 팬 여부에 따라서 필터링).
const FAN_FILTERS = ['all', 'fan', 'nonfan'];

function formatRelative(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const diff = Date.now() - date.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString();
}

function formatCount(n) {
  const num = Number(n || 0);
  if (num >= 1000) return `${(num / 1000).toFixed(num >= 10000 ? 0 : 1)}k`;
  return String(num);
}

export default function CelebRoomFeed() {
  const { userId, accessToken } = useDmPersona();

  const [queryInput, setQueryInput] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [sort, setSort] = useState('fans');
  const [fanFilter, setFanFilter] = useState('all');

  const query = useInfiniteQuery({
    queryKey: ['celeb-room', 'feed', userId, submittedQuery, sort],
    queryFn: ({ pageParam = 0 }) =>
      getCelebRoomsFeed(
        { page: pageParam, size: PAGE_SIZE, query: submittedQuery, sort },
        accessToken
      ),
    getNextPageParam: (last) => {
      const loaded = (last.page + 1) * last.size;
      return loaded < last.total ? last.page + 1 : undefined;
    },
    initialPageParam: 0,
    refetchOnWindowFocus: true,
    staleTime: 30 * 1000,
  });

  const items = useMemo(
    () => (query.data?.pages || []).flatMap((p) => p.items),
    [query.data]
  );

  // 팬 여부 필터는 이미 로드된 페이지에 대해 UI에서 적용됩니다.
  const visibleItems = useMemo(() => {
    if (fanFilter === 'fan') return items.filter((r) => r.is_fan);
    if (fanFilter === 'nonfan') return items.filter((r) => !r.is_fan);
    return items;
  }, [items, fanFilter]);

  const onSearch = (e) => {
    e.preventDefault();
    setSubmittedQuery(queryInput.trim());
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-white flex items-center gap-2">
          <Radio size={20} className="text-primary" />
          {t('celebRoom.feed.title')}
        </h1>
        <Link
          to="/celeb-room/mine"
          className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg bg-dark-surface border border-dark-border text-dark-text-secondary text-sm font-medium hover:border-primary/40 hover:text-primary transition-colors"
        >
          <Home size={16} />
          {t('celebRoom.feed.myRoom')}
        </Link>
      </div>

      <form onSubmit={onSearch} className="flex items-center gap-2 mb-3">
        <div className="relative flex-grow">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-text-muted"
          />
          <input
            type="text"
            value={queryInput}
            onChange={(e) => setQueryInput(e.target.value)}
            placeholder={t('celebRoom.feed.searchPlaceholder')}
            className="w-full bg-dark-surface border border-dark-border rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 rounded-lg bg-primary text-black text-sm font-bold hover:bg-primary/90 transition-colors"
        >
          {t('celebRoom.feed.search')}
        </button>
      </form>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-5">
        {/* 팬 여부 필터 */}
        <div className="inline-flex items-center rounded-lg bg-dark-surface border border-dark-border p-0.5">
          {FAN_FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFanFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                fanFilter === f
                  ? 'bg-primary text-black'
                  : 'text-dark-text-secondary hover:text-primary'
              }`}
            >
              {t(`celebRoom.feed.fanFilter.${f}`)}
            </button>
          ))}
        </div>

        {/* 정렬 */}
        <label className="inline-flex items-center gap-2 text-xs text-dark-text-secondary">
          {t('celebRoom.feed.sortLabel')}
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="bg-dark-surface border border-dark-border rounded-lg px-2.5 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            {SORT_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {t(`celebRoom.feed.sortOptions.${s}`)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {query.isLoading ? (
        <div className="text-center py-16 text-dark-text-secondary">{t('common.loading')}</div>
      ) : query.isError ? (
        <div className="text-center py-12 px-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm">
          {query.error?.message || t('celebRoom.feed.error')}
        </div>
      ) : visibleItems.length === 0 ? (
        <div className="text-center py-16 px-6 rounded-2xl bg-dark-card border border-dark-border">
          <Radio size={32} className="mx-auto mb-3 text-dark-text-muted" />
          <p className="text-sm text-dark-text-secondary">
            {items.length === 0 ? t('celebRoom.feed.empty') : t('celebRoom.feed.emptyFilter')}
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {visibleItems.map((room) => {
            const celeb = room.celeb || {};
            return (
              <li key={room.room_id}>
                <Link
                  to={`/celeb-room/r/${room.room_id}`}
                  state={{ celebUserId: celeb.user_id }}
                  className="flex items-center gap-3 p-3 sm:p-4 rounded-xl bg-dark-card border border-dark-border hover:border-primary/40 transition-colors"
                >
                  <UrlAvatar
                    url={celeb.profile_photo || celeb.profile_photo_url}
                    name={celeb.name || celeb.username}
                    size={48}
                    className="w-11 h-11 sm:w-12 sm:h-12"
                  />
                  <div className="min-w-0 flex-grow">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-white truncate">
                        {celeb.name || celeb.username || `#${celeb.user_id}`}
                        {celeb.username && (
                          <span className="ml-1.5 text-xs font-normal text-dark-text-muted">
                            @{celeb.username}
                          </span>
                        )}
                        {room.is_fan && (
                          <span className="ml-1.5 inline-flex items-center gap-0.5 align-middle text-[10px] font-bold text-primary">
                            <Star size={10} className="fill-primary" />
                            {t('celebRoom.feed.fanBadge')}
                          </span>
                        )}
                      </p>
                      <span className="text-[11px] text-dark-text-muted flex-shrink-0">
                        {formatRelative(room.last_timestamp)}
                      </span>
                    </div>
                    <p className="text-xs text-dark-text-secondary truncate">
                      {room.last_message || t('celebRoom.feed.noMessage')}
                    </p>
                    <div className="mt-1 flex items-center gap-3 text-[11px] text-dark-text-muted">
                      <span className="inline-flex items-center gap-1">
                        <Users size={12} /> {formatCount(room.enter_fan_count)}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Eye size={12} /> {formatCount(room.view_count)}
                      </span>
                    </div>
                  </div>
                  {room.is_read ? (
                    <CheckCircle2 size={16} className="text-dark-text-muted shrink-0" />
                  ) : (
                    <Circle size={10} className="text-primary fill-primary shrink-0" />
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {query.hasNextPage && (
        <div className="mt-6 text-center">
          <button
            type="button"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="px-4 py-2 rounded-lg bg-dark-surface border border-dark-border text-dark-text-secondary text-sm hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-50"
          >
            {query.isFetchingNextPage ? t('common.loading') : t('celebRoom.feed.loadMore')}
          </button>
        </div>
      )}
    </div>
  );
}
